/**
 * Linking THIS app to a surveyor, from inside it (DECISIONS D-019).
 *
 * ── The defect this is the answer to ────────────────────────────────────────
 * iOS gives a Home Screen web app its own storage container. A setup link
 * tapped in Messages opens in Safari, so the token lands in Safari's container
 * and the installed app — the one used in the field — is still unlinked and
 * pulls nothing. Before this component the installed app had NO way to link
 * itself: `claimTokenFromLocation()` and `claimOnboardingFromLocation()` both
 * need the app to have been opened BY the link, which on iOS it never is.
 *
 * ── Two ways in, because one of them will fail in a machine room ────────────
 * SCAN is first and is the big button: the camera permission is already
 * familiar from taking survey photos, and pointing a phone at a screen is not a
 * skill anyone has to be taught.
 *
 * TYPE is directly underneath, at the same size, always visible — not hidden
 * behind "having trouble?". The camera fails for reasons nobody in the office
 * can see: a lens scratched by a machine room, glare across a tilted laptop
 * screen, a permission somebody refused two years ago, hands that will not hold
 * a frame still. A fallback a stuck surveyor has to discover is not a fallback.
 * It carries the same single-use, 24-hour, dies-on-re-issue guarantees, because
 * it is the same record (D-013, extended by D-019).
 *
 * ── Nothing here renders a credential ───────────────────────────────────────
 * The scanned secret goes from the decoder into a redemption and is never put
 * on screen. The typed code IS on screen, because a code nobody can read is not
 * a code anybody can type — that narrowing, and why it does not touch D-005, is
 * recorded in D-019. The access TOKEN it is exchanged for is written to storage
 * and never displayed, logged or copied anywhere.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { QrScanner, type ScannerProblem } from './QrScanner';
import { formatPairCode, normalizePairCode, pairCodeProblem } from '../../core/pairing';
import { OnboardingError } from '../../db/onboarding-client';
import {
  onboardingSecretFromScan,
  redeemScannedSecret,
  redeemTypedPairCode,
  restartLinked,
} from '../state/onboardingClaim';
import { onboardView } from '../state/onboardingView';

type Stage =
  /** The two doors: a Scan button and a code field. */
  | { at: 'choosing' }
  /** The camera is open. */
  | { at: 'scanning' }
  /** A code is being exchanged for a token. */
  | { at: 'redeeming' }
  /** It was refused, or the office is unreachable. */
  | { at: 'failed'; code: string; message: string };

export function PairDevice(): ReactNode {
  const [stage, setStage] = useState<Stage>({ at: 'choosing' });
  const [typed, setTyped] = useState('');
  /** A problem with what was typed, or with what was scanned. Local, not fatal. */
  const [hint, setHint] = useState<string | null>(null);

  const attempt = useCallback(async (redeem: () => Promise<unknown>): Promise<void> => {
    setStage({ at: 'redeeming' });
    setHint(null);
    try {
      await redeem();
      // The token is already on the device — `onboardingClaim` writes it before
      // resolving, so nothing can be dropped between here and the reload.
      restartLinked();
    } catch (cause) {
      const code = cause instanceof OnboardingError ? cause.code : 'unknown';
      const message = cause instanceof Error ? cause.message : '';
      setStage({ at: 'failed', code, message });
    }
  }, []);

  const onScanned = useCallback(
    (decoded: string): void => {
      const secret = onboardingSecretFromScan(decoded);
      if (secret === null) {
        // Not a refusal and not an error — the camera found A code, just not
        // this one. Back to the choice with a sentence that says what to point
        // at, rather than a dead end.
        setStage({ at: 'choosing' });
        setHint(
          'That was not a setup code. Point the camera at the square code on the office ' +
            'screen, or type the letters underneath it.',
        );
        return;
      }
      void attempt(() => redeemScannedSecret(secret));
    },
    [attempt],
  );

  /**
   * Carry the camera's failure back to the field.
   *
   * The scanner explains itself where it stands, but the surveyor then leaves
   * that screen — and arriving at a code field with no idea why the camera
   * stopped working reads as the app having changed its mind. One sentence,
   * above the field, so the typed code is understood as the answer to
   * something rather than as a second thing to try.
   */
  const onScannerProblem = useCallback((problem: ScannerProblem): void => {
    setHint(
      problem === 'denied'
        ? 'The camera is switched off for this app, so type the nine letters instead.'
        : 'The camera could not be used, so type the nine letters instead.',
    );
  }, []);

  /* ─────────────────────────────── redeeming ─────────────────────────────── */

  if (stage.at === 'redeeming') {
    return (
      <div className="card" role="status" aria-live="polite">
        <p className="q" data-legibility="question">
          Setting up this phone…
        </p>
        <p className="ruleline" data-legibility="helper">
          One moment. This only takes a second.
        </p>
      </div>
    );
  }

  /* ──────────────────────────────── refused ──────────────────────────────── */

  if (stage.at === 'failed') {
    const view = onboardView({
      phase: stage.code === 'offline' ? 'offline' : 'refused',
      errorCode: stage.code,
      errorMessage: stage.message,
    });
    return (
      <div className="card" role="status" aria-live="polite">
        <p className="q" data-legibility="question">
          {view.headline}
        </p>
        <p className="ruleline" data-legibility="helper">
          {view.detail}
        </p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            // OFFLINE keeps what they typed. Nothing was spent, the code on the
            // office screen still works, and making a 60-year-old retype nine
            // letters because the app could not find a bar of signal is a
            // punishment for the building's steel. A REFUSAL clears it: that
            // code is dead and the next step is a different one.
            if (stage.code !== 'offline') setTyped('');
            setHint(null);
            setStage({ at: 'choosing' });
          }}
        >
          {stage.code === 'offline' ? 'Try again' : 'Try another code'}
        </button>
      </div>
    );
  }

  /* ──────────────────────────────── scanning ─────────────────────────────── */

  if (stage.at === 'scanning') {
    return (
      <QrScanner
        onDecoded={onScanned}
        onCancel={() => setStage({ at: 'choosing' })}
        onProblem={onScannerProblem}
      />
    );
  }

  /* ──────────────────────────────── choosing ─────────────────────────────── */

  return (
    <>
      <div className="card">
        {/* Instruction above its control, at full helper size (D-011 item 6). */}
        <p className="leadin" data-legibility="helper">
          Ask for the setup code on the office screen. It shows a square code and nine letters
          underneath it — either one works.
        </p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            setHint(null);
            setStage({ at: 'scanning' });
          }}
        >
          Scan the code
        </button>
      </div>

      <div className="card">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const problem = pairCodeProblem(typed);
            if (problem !== null) {
              setHint(problem);
              return;
            }
            void attempt(() => redeemTypedPairCode(typed));
          }}
        >
          {/* ABOVE the field it is about (D-011 item 6). Both things that land
              here are instructions — "the camera is off, type it instead" and
              "a setup code never uses A, E, I, O or U" — and an instruction
              printed under the box has already been scrolled past. */}
          <div aria-live="polite">
            {hint ? (
              <p className="leadin" data-legibility="helper">
                {hint}
              </p>
            ) : null}
          </div>

          <div className="field">
            <label className="field__label" data-legibility="question" htmlFor="pair-code">
              Or type the nine letters
            </label>
            <p className="field__hint" data-legibility="helper" id="pair-code-hint">
              Exactly as they appear on the office screen. Dashes and capitals do not matter.
            </p>
            <input
              id="pair-code"
              className="field__input field__input--mono"
              type="text"
              value={typed}
              placeholder="BCD-FGH-JKL"
              /* Letters only, so the keyboard never has to change mode. The
                 four attributes below are what stop a phone from "helping":
                 autocorrect turning a code into a word, autocapitalize
                 fighting the formatter, and a spellchecker underlining all of
                 it in red on a screen that is meant to look calm. */
              inputMode="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
              aria-describedby="pair-code-hint"
              onChange={(event) => {
                // Re-grouped as they type, so what is on the phone looks like
                // what is on the office screen and can be compared at a glance.
                // Normalising first means a paste of any shape lands right.
                setTyped(formatPairCode(event.target.value));
                setHint(null);
              }}
            />
          </div>

          <button
            type="submit"
            className="btn btn--ghost"
            disabled={normalizePairCode(typed).length === 0}
          >
            Set up this phone
          </button>
        </form>
      </div>
    </>
  );
}
